import * as React from 'react';

import { Translate } from 'react-localize-redux';

import { Dispatch } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { push, CallHistoryMethodAction } from 'connected-react-router';
import {
  Switch,
  useRouteMatch,
  Route,
  Redirect,
  useLocation,
} from 'react-router-dom';

import AppState from '@webapp/store/state';
import { artistSiteUrl } from '@webapp/store/artist/selectors';

import SidebarCtrl, { SidebarSection } from '@webapp/controllers/sidebar';
import SmallNotification from '@components/small-notification';
import FormContainer from '@components/layout/form-container';
import SidebarWrapper from '@components/layout/sidebar-wrapper';
import SidebarContainer from '@components/layout/sidebar-container';

import ArtistProfileInfoPage from './artist-info/index';
import ArtistProfileFinancialPage from './artist-financials/index';
import ArtistProfileTeamPage from './artist-team';
import ArtistDeactivatePage from './deactivate-artist';
import ArtistPresetsPage from './artist-presets';
import ArtistSubscriptions from './artist-subscriptions';
import ArtistInvoices from './artist-invoices';
import ArtistPayoutPage from './artist-payout';

import TabsNav, { TabProp } from '@components/tabs-nav';
import styled from 'styled-components';

interface ArtistProfilePageProps {
  className?: string;
  siteUrl: string;
  hideSidebar?: boolean;
  isMobile?: boolean;
  goTotrac: () => CallHistoryMethodAction;
}

type ArtistProfileTabs = 'profile' | 'subscriptions' | 'team' | 'presets';

export const artistProfileSections: SidebarSection[] = [
  {
    path: 'profile',
    label: <Translate id="ARTIST_PROFILE.PROFILE.TITLE" />,
    noIcon: true,
    subItems: [
      // TODO: DEVTEAM-845 - Make it scroll to each section when clicked.
      {
        path: 'general',
        label: <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TITLE" />,
      },
      {
        path: 'links',
        label: <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS" />,
      },
      {
        path: 'tour',
        label: <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR" />,
      },
      {
        path: 'integrations',
        label: (
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.INTEGRATIONS" />
        ),
        disabled: true,
      },
    ],
  },
  {
    path: 'subscriptions',
    label: <Translate id="ARTIST_PROFILE.SUBSCRIPTION.TITLE" />,
    noIcon: true,
  },
  {
    path: 'team',
    label: <Translate id="ARTIST_PROFILE.TEAM.TITLE" />,
    noIcon: true,
  },
  {
    path: 'presets',
    label: <Translate id="ARTIST_PROFILE.PRESETS.TITLE" />,
    noIcon: true,
  },
];

const initialTab = 0;

const mapStateToProps = (state: AppState) => ({
  siteUrl: artistSiteUrl(state),
  hideSidebar:
    state.router.location.pathname.endsWith('/deactivate') ||
    state.router.location.pathname.startsWith('/artist/subscriptions/invoices'),
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTotrac: () => dispatch(push(`/artist/page/settings`)),
});

export const ArtistProfilePage: React.FC<ArtistProfilePageProps> = (props) => {
  const match = useRouteMatch<{ account: string }>();

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );

  const tabsData: TabProp<ArtistProfileTabs>[] = React.useMemo(
    () => [
      {
        label: <Translate id="ARTIST_PROFILE.PROFILE.TITLE" />,
        key: 'profile' as ArtistProfileTabs,
      },
      {
        label: <Translate id="ARTIST_PROFILE.SUBSCRIPTION.TITLE" />,
        key: 'subscriptions' as ArtistProfileTabs,
      },
      {
        label: <Translate id="ARTIST_PROFILE.TEAM.TITLE" />,
        key: 'team' as ArtistProfileTabs,
      },
      {
        label: <Translate id="ARTIST_PROFILE.PRESETS.TITLE" />,
        key: 'presets' as ArtistProfileTabs,
      },
    ],
    []
  );

  const [activeTab, setActiveTab] = React.useState<ArtistProfileTabs>(
    tabsData[initialTab].key
  );

  const location = useLocation();

  const getActiveTab = (url: string) => {
    const urlArray = url.split('/');
    const lastUrl = urlArray[urlArray.length - 1];
    return lastUrl;
  };

  React.useEffect(() => {
    if (getActiveTab(location.pathname) === 'profile') {
      setActiveTab(tabsData[0].key);
    } else if (getActiveTab(location.pathname) === 'subscriptions') {
      setActiveTab(tabsData[1].key);
    } else if (getActiveTab(location.pathname) === 'team') {
      setActiveTab(tabsData[2].key);
    } else if (getActiveTab(location.pathname) === 'presets') {
      setActiveTab(tabsData[3].key);
    } else {
      setActiveTab(tabsData[0].key);
    }
  }, [tabsData, location.pathname]);

  const dispatch = useDispatch();

  const handleTabClick = (value: ArtistProfileTabs) => {
    setActiveTab(value);
    dispatch(push(`/artist/${value}`));
  };

  return (
    <div className={props.className}>
      <SidebarContainer>
        {props.hideSidebar ? null : props.isMobile ? (
          <TabsNav
            className="tab-nav"
            tabs={tabsData}
            currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
            onChange={(tab) => handleTabClick(tab.key as ArtistProfileTabs)}
            bigFont
            activeBigFont
            overflow
          />
        ) : (
          <SidebarWrapper>
            <SidebarCtrl
              title={<Translate id="ARTIST.EDITOR.TITLE" />}
              sections={artistProfileSections}
            />
          </SidebarWrapper>
        )}

        <FormContainer hasSidebar={!props.hideSidebar}>
          <Switch>
            <Route
              exact
              path={`${match.path}/profile`}
              render={() => <ArtistProfileInfoPage />}
            />
            <Route
              exact
              path={`${match.path}/subscriptions`}
              render={() => <ArtistSubscriptions />}
            />
            <Route
              exact
              path={`${match.path}/subscriptions/invoices`}
              render={() => <ArtistInvoices />}
            />
            <Route
              exact
              path={`${match.path}/team`}
              render={() => <ArtistProfileTeamPage />}
            />
            <Route
              exact
              path={`${match.path}/billing`}
              render={() => <ArtistProfileFinancialPage />}
            />
            <Route
              exact
              path={`${match.path}/earnings`}
              render={() => <ArtistPayoutPage />}
            />
            <Route
              exact
              path={`${match.path}/deactivate`}
              render={() => <ArtistDeactivatePage />}
            />
            <Route
              exact
              path={`${match.path}/presets`}
              render={() => <ArtistPresetsPage />}
            />
            <Route
              exact
              path={match.path}
              render={() => <Redirect to={`${match.path}/profile`} />}
            />
          </Switch>
        </FormContainer>
        {toastNotification?.value === 'user_changes_saved' && (
          <SmallNotification
            notificationType={toastNotification.type}
            title={<Translate id={toastNotification.label} />}
          />
        )}
      </SidebarContainer>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistProfilePage)`
  .tab-nav {
    padding-inline: 24px;
  }
`);
